import { useEffect } from 'react'

import * as d3 from 'd3'
import * as geometric from 'geometric'

import { fontFamilies } from '@hivewatch/dark'

import { DeviceType } from '@/graphql/generated/schemas'

import {
  createCameraViewPath,
  getCheckPointIcon,
  getEditCheckPointColor,
  getEditCheckPointGlyph,
  getEditCheckPointIcon,
  getEditCheckPointText,
  getEditCheckPointWidth,
  getRemoveIconOpacity,
  getTailgateConnectionElementId,
  handleCheckPointDeviceDrag,
} from '../utils/chart'
import { angleDrag, angleWidthDrag, distanceDrag } from '../utils/drag'
import {
  FLOOR_PLAN_ASSOCIATION_GLYPH_GEOMETRY,
  FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS,
  FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE,
  FLOOR_PLAN_CHECKPOINT_ACTION_OPACITY,
  FLOOR_PLAN_DEVICE_GLYPH_SIZE_RATIO,
  getFloorPlanCanvasAssociationColors,
  getFloorPlanCanvasMarkerColors,
  getFloorPlanVisualColors,
} from '../utils/floorPlanVisuals'

const IMAGE_PADDING = 80

/**
 * Device icon sizes are expressed in floor-plan asset pixels — the SVG <image>
 * is drawn at the asset's natural dimensions, so one user unit inside
 * `.map-group` is one asset pixel. An icon's size relative to the building is
 * therefore `iconSize / imageWidth`; the viewport and the zoom level cancel out.
 *
 * Calibrated against the normalized asset every upload now produces — longest
 * side exactly 3500px, see MAX_DIMENSION in floor-crud/utils/imageUtils.ts
 * (ENG-27100). On that baseline these map to roughly 10 / 24 / 75 screen px at
 * the fit-to-container view. The previous 7.5 / 24 / 75 values predated that
 * normalization and rendered ~3x smaller on it, which is what ENG-34662 fixes.
 *
 * These live here rather than in FloorChartContainer so that
 * selectFloorPlanIconSize (redux/ui/uiSlice) can clamp persisted sizes into the
 * same range the sliders present.
 */
export const MIN_FLOOR_PLAN_ICON_SIZE = 30
export const MAX_FLOOR_PLAN_ICON_SIZE = 225
export const DEFAULT_ICON_SIZE = 72

const getCameraControlId = (deviceId) => `${deviceId}Control`

const setCameraControlClass = (deviceId, className, enabled) => {
  const cameraControl = document.getElementById(getCameraControlId(deviceId))
  if (cameraControl) d3.select(cameraControl).classed(className, enabled)
}

export const useFloorChart = ({
  floorPlanIconSize,
  devices,
  checkpoints,
  connections,
  interactionsEnabled,
  onDeviceDragStart = (_device) => {},
  onDeviceUpdated,
  onDeviceReleased,
  onDeviceClicked,
  onDeviceHover,
  onDeviceHoverOut,
  onForceCloseHoverCards = () => {},
  onRemoveCheckpoint,
  onMoveCheckpoint,
  onConnectionButtonClicked,
  highlightedDeviceIds,
  tourStopDeviceIds = [],
  visualVariant = 'legacy',
  colorScheme = 'dark',
}) => {
  const isRebrand = visualVariant === 'rebrand'
  const canvasMarkerColors = getFloorPlanCanvasMarkerColors()
  const themedColors = getFloorPlanVisualColors(colorScheme)

  const onZoom = ({ transform }) => {
    // Can get zoom level with `transform.k`
    d3.select('.map-group').attr('transform', transform)
    // Markers move under a stationary pointer here, which never fires a
    // mouseout — clear any open hover card explicitly, even if the pointer
    // is resting on the card itself.
    onForceCloseHoverCards()
  }

  const getZoom = (minScale) => {
    return d3.zoom().scaleExtent([minScale, 10]).on('zoom', onZoom)
  }

  // This is only called when _moving_ a Device - not when adjusting a
  // Camera's view cone. For that, look to the below angleDrag, distanceDrag,
  // & angleWidthDrag
  const handleDeviceDrag = d3
    .drag()
    .on('start', (_event, d) => {
      if (interactionsEnabled) onDeviceDragStart(d)
    })
    .on('drag', (event, d) => {
      if (interactionsEnabled) {
        d.x = event.x
        d.y = event.y
        d.dragged = true
        onDeviceUpdated(d)
      }
    })
    .on('end', (event, d) => {
      if (interactionsEnabled) {
        d.x = event.x
        d.y = event.y
        d.dragged = false
        onDeviceReleased(d)
      }
    })

  const handleRotationDrag = angleDrag((d, angle) => {
    if (!interactionsEnabled) return
    d.rotation = angle
    onDeviceUpdated(d)
  }, onDeviceReleased)

  const handleDistanceDrag = distanceDrag((d, length) => {
    if (!interactionsEnabled) return
    d.distance = length
    onDeviceUpdated(d)
  }, onDeviceReleased)

  const handleAngleWidthDrag = angleWidthDrag((d, angle) => {
    if (!interactionsEnabled) return
    d.angle = angle
    onDeviceUpdated(d)
  }, onDeviceReleased)

  const calculateRotationHandleTransform = (d) => {
    const position = geometric.pointTranslate(
      [0, 0],
      d.rotation - 90,
      d.distance + 100 // 100 is spacing between handle and the device
    )
    return `translate(${position}) rotate(${d.rotation})`
  }

  const calculateDistanceHandleTransform = (d) => {
    const position = geometric.pointTranslate(
      [0, 0],
      d.rotation - 90,
      d.distance
    )
    return `translate(${position}) rotate(${d.rotation + 90})`
  }

  const calculateAngleWidthHandleTransform = (d) => {
    const position = geometric.pointTranslate(
      [0, 0],
      d.rotation - 90 + d.angle * 0.5,
      d.distance
    )
    return `translate(${position}) rotate(${d.rotation + d.angle * 0.5})`
  }

  const isHandleVisible = (d) => {
    return d.isEditMode ? 'visible' : 'hidden'
  }

  const getConnectionIcon = (d) => {
    if (d.deviceType === DeviceType.Alarm) {
      return 'alarmAssociation.png'
    }
    return d.isConnected && d.isTailgateEnabled
      ? 'TailgateGreen.png'
      : 'TailgateWhite.png'
  }

  const connectionLine = (lineCoords) => {
    const line = d3.line()
    return line(lineCoords)
  }

  const getMarkerGlyphSize = (size) => size * FLOOR_PLAN_DEVICE_GLYPH_SIZE_RATIO

  const getMarkerGlyphTransform = (size, rotation = 0) => {
    const glyphSize = getMarkerGlyphSize(size)
    const scale = glyphSize / 20
    return `rotate(${rotation}) translate(${-glyphSize / 2}, ${
      -glyphSize / 2
    }) scale(${scale})`
  }

  const getTourStopOrdinal = (deviceId) => {
    const index = tourStopDeviceIds?.indexOf(deviceId) ?? -1
    return index >= 0 ? index + 1 : ''
  }

  const getAssociationGlyphTransform = (d, size, leftRatio) => {
    const glyphSize = size * FLOOR_PLAN_ASSOCIATION_GLYPH_GEOMETRY.sizeRatio
    const glyphScale = glyphSize / 20
    const x = d.midX - size / 2 + size * leftRatio
    const y =
      d.midY - size / 2 + size * FLOOR_PLAN_ASSOCIATION_GLYPH_GEOMETRY.topRatio

    return `translate(${x}, ${y}) scale(${glyphScale})`
  }

  const updateRebrandAssociationMarker = (selection, size) => {
    selection
      .select('.connectionLine')
      .attr('stroke', (d) => getFloorPlanCanvasAssociationColors(d).line)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4 4')
      .attr('stroke-linecap', 'butt')

    selection
      .select('.connectionCircle')
      .attr('cx', (d) => d.midX)
      .attr('cy', (d) => d.midY)
      .attr('r', size / 2)
      .attr('fill', (d) => getFloorPlanCanvasAssociationColors(d).background)
      .attr('stroke', 'none')

    selection
      .select('.associationCameraGlyph')
      .attr('color', (d) => getFloorPlanCanvasAssociationColors(d).glyph)
      .attr('transform', (d) =>
        getAssociationGlyphTransform(
          d,
          size,
          FLOOR_PLAN_ASSOCIATION_GLYPH_GEOMETRY.cameraLeftRatio
        )
      )

    selection
      .select('.associationDeviceGlyph')
      .attr('color', (d) => getFloorPlanCanvasAssociationColors(d).glyph)
      .attr('transform', (d) =>
        getAssociationGlyphTransform(
          d,
          size,
          FLOOR_PLAN_ASSOCIATION_GLYPH_GEOMETRY.deviceLeftRatio
        )
      )
      .select('use')
      .attr('href', (d) =>
        d.deviceType === DeviceType.Alarm
          ? '#floor-plan-alarm-glyph'
          : '#floor-plan-door-glyph'
      )
  }

  const appendRebrandAssociationMarker = (selection, size) => {
    const glyphs = selection.append('g').attr('class', 'connectionImage')

    glyphs
      .append('g')
      .attr('class', 'associationCameraGlyph')
      .append('use')
      .attr('href', '#floor-plan-camera-glyph')

    glyphs.append('g').attr('class', 'associationDeviceGlyph').append('use')

    updateRebrandAssociationMarker(selection, size)
  }

  const appendRebrandDeviceMarker = (g, iconId, size, getRotation) => {
    g.append('circle')
      .attr('class', 'cameraCircle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', size / 2)

    g.append('circle')
      .attr('class', 'deviceMarkerRing')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', size / 2 - 1)

    g.append('g')
      .attr('class', 'cameraIconSvg')
      .attr('color', canvasMarkerColors.markerGlyph)
      .attr('transform', (d) =>
        getMarkerGlyphTransform(size, getRotation?.(d) ?? 0)
      )
      .append('use')
      .attr('href', `#floor-plan-${iconId}-glyph`)
  }

  const updateRebrandDeviceMarker = (update, size, iconId, getRotation) => {
    update.select('.cameraCircle').attr('r', size / 2)
    update.select('.deviceMarkerRing').attr('r', size / 2 - 1)
    update
      .select('.cameraIconSvg')
      .attr('color', canvasMarkerColors.markerGlyph)
      .attr('transform', (d) =>
        getMarkerGlyphTransform(size, getRotation?.(d) ?? 0)
      )
      .select('use')
      .attr('href', `#floor-plan-${iconId}-glyph`)
  }

  // region Camera
  // For some reason this param should be doubled to match other icon sizes
  const cameraEnter = (enter, size) => {
    // Calculate derived sizes
    const halfSize = size / 2
    const circleRadius = halfSize
    const iconSize = size * (24 / 44) // Scale icon proportionally to original ratio
    const halfIconSize = iconSize / 2

    const g = enter
      .append('g')
      .attr('class', 'camera')
      .classed('editOn', (d) => d.isEditMode)
      .attr('id', (d) => {
        return `device-${d.id}`
      })
      .attr('transform', (d) => `translate(${d.x}, ${d.y})`)
      .on('click', (event, d) => {
        onDeviceClicked(d)
        event.stopPropagation()
      })
      .on('mouseover', (event, d) => {
        onDeviceHover(d, d.id, 'camera')
        if (isRebrand) setCameraControlClass(d.id, 'isFovHovered', true)
      })
      .on('mouseout', (event, d) => {
        onDeviceHoverOut(d, d.id, 'camera')
        if (isRebrand) setCameraControlClass(d.id, 'isFovHovered', false)
      })
      .call(handleDeviceDrag)

    if (isRebrand) {
      appendRebrandDeviceMarker(g, 'camera', size, (d) => d.rotation - 90)
      return
    }

    g.append('circle')
      .attr('class', 'cameraCircle')
      .attr('transform', (d) => `rotate(${d.rotation})`)
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', circleRadius)

    g.append('image')
      .attr('class', 'cameraIconSvg')
      // -90 only because the image is pointing East
      .attr('transform', (d) => `rotate(${d.rotation - 90})`)
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('href', 'video.png')
      .attr('x', -halfIconSize)
      .attr('y', -halfIconSize)
  }

  const cameraUpdate = (update, iconSize) => {
    if (isRebrand) {
      updateRebrandDeviceMarker(
        update,
        iconSize,
        'camera',
        (d) => d.rotation - 90
      )
    } else {
      update
        .select('.cameraIconSvg')
        // -90 only because the image is pointing East
        .attr('transform', (d) => `rotate(${d.rotation - 90})`)
        .attr('width', `${iconSize / 2}px`)
        .attr('height', `${iconSize / 2}px`)
        .attr('x', -iconSize / 4)
        .attr('y', -iconSize / 4)

      update.select('.cameraCircle').attr('r', iconSize / 2) // Update circle radius
    }

    update.attr('id', (d) => {
      return `device-${d.id}`
    })
    update.attr(
      'transform',
      (d) => `translate(${d.projectedX}, ${d.projectedY})`
    )

    update.classed('invalidPosition', (d) => d.invalidPosition)
    update.classed('editOn', (d) => d.isEditMode)
    update.classed('dragged', (d) => d.dragged)
    update.classed('isHighlighted', (d) =>
      highlightedDeviceIds?.find((id) => d.id === id)
    )
  }

  // For when you're able to adjust the field of view for a camera
  const cameraControlEnter = (enter, size) => {
    // Calculate half size for positioning elements
    const halfSize = size / 2

    const g = enter
      .append('g')
      .attr('class', 'cameraControl')
      .classed('editOn', (d) => d.isEditMode)
      .classed(
        'isFovFocused',
        (d) =>
          isRebrand && (d.isEditMode || highlightedDeviceIds?.includes(d.id))
      )
      .attr('id', (d) => getCameraControlId(d.id))
      .attr('transform', (d) => `translate(${d.projectedX}, ${d.projectedY})`)
      .on('click', (event, d) => {
        onDeviceClicked(d)
        event.stopPropagation()
      })
      .call(handleDeviceDrag)

    g.append('path')
      .datum((d) => createCameraViewPath(d.distance, d.rotation, d.angle))
      .attr('d', d3.arc())
      .attr('class', 'cameraView')

    g.append('image')
      .attr('class', 'floorDeviceHandle handleRotation')
      .attr('visibility', isHandleVisible)
      .attr('width', `${size}px`)
      .attr('height', `${size}px`)
      .attr('href', 'ArrowHandleCorner.png')
      .attr('x', 0 - halfSize)
      .attr('y', 0 - halfSize)
      .attr('transform', calculateRotationHandleTransform)
      .call(handleRotationDrag)

    g.append('image')
      .attr('class', 'floorDeviceHandle handleDistance')
      .attr('visibility', isHandleVisible)
      .attr('width', `${size}px`)
      .attr('height', `${size}px`)
      .attr('href', 'ArrowHandle.png')
      .attr('x', 0 - halfSize)
      .attr('y', 0 - halfSize)
      .attr('transform', calculateDistanceHandleTransform)
      .call(handleDistanceDrag)

    g.append('image')
      .attr('class', 'floorDeviceHandle handleAngleWidth')
      .attr('visibility', isHandleVisible)
      .attr('width', `${size}px`)
      .attr('height', `${size}px`)
      .attr('href', 'ArrowHandle.png')
      .attr('x', 0 - halfSize)
      .attr('y', 0 - halfSize)
      .attr('transform', calculateAngleWidthHandleTransform)
      .call(handleAngleWidthDrag)
  }

  const cameraControlUpdate = (update, iconSize) => {
    // No need to scale path as it's generated by d3.arc()
    update
      .select('path')
      .datum((d) => createCameraViewPath(d.distance, d.rotation, d.angle))
      .attr('d', d3.arc())

    // Scale handler images
    update
      .select('.handleRotation')
      .attr('visibility', isHandleVisible)
      .attr('transform', calculateRotationHandleTransform)
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)

    update
      .select('.handleDistance')
      .attr('visibility', isHandleVisible)
      .attr('transform', calculateDistanceHandleTransform)
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)

    update
      .select('.handleAngleWidth')
      .attr('visibility', isHandleVisible)
      .attr('transform', calculateAngleWidthHandleTransform)
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)

    update.attr('transform', (d) => `translate(${d.x}, ${d.y})`)

    update.classed('invalidPosition', (d) => d.invalidPosition)
    update.classed('editOn', (d) => d.isEditMode)
    update.classed('dragged', (d) => d.dragged)
    update.classed(
      'isFovFocused',
      (d) => isRebrand && (d.isEditMode || highlightedDeviceIds?.includes(d.id))
    )
  }
  // endregion

  const doorEnter = (enter, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 24

    // Base dimensions that will be scaled
    const baseCircleRadius = 22

    const g = enter
      .append('g')
      .attr('class', 'door')
      .classed('editOn', (d) => d.isEditMode)
      .attr('id', (d) => `device-${d.id}`)
      .attr(
        'transform',
        (d) =>
          `translate(${d.projectedX}, ${
            isRebrand ? d.projectedY : d.projectedX
          })`
      )
      .on('click', (event, d) => {
        onDeviceClicked(d)
        event.stopPropagation()
      })
      .on('mouseover', (event, d) => {
        onDeviceHover(d, d.id, 'door')
      })
      .on('mouseout', (event, d) => {
        onDeviceHoverOut(d, d.id, 'door')
      })
      .call(handleDeviceDrag)

    if (isRebrand) {
      appendRebrandDeviceMarker(g, 'door', iconSize)
      return
    }

    g.append('circle')
      .attr('class', 'cameraCircle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', baseCircleRadius * scaleFactor)

    g.append('image')
      .attr('class', 'cameraIconSvg')
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('href', 'door.png')
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)
  }

  const doorUpdate = (update, iconSize) => {
    if (isRebrand) {
      updateRebrandDeviceMarker(update, iconSize, 'door')
    } else {
      // Calculate scale factor based on the provided icon size
      const scaleFactor = iconSize / 24

      update
        .select('.cameraIconSvg')
        .attr('width', `${iconSize}px`)
        .attr('height', `${iconSize}px`)
        .attr('x', -iconSize / 2)
        .attr('y', -iconSize / 2)

      update.select('.cameraCircle').attr('r', 22 * scaleFactor)
    }

    update.attr(
      'transform',
      (d) => `translate(${d.projectedX}, ${d.projectedY})`
    )
    update.attr('id', (d) => {
      return `device-${d.id}`
    })

    update.classed('invalidPosition', (d) => d.invalidPosition)
    update.classed('editOn', (d) => d.isEditMode)
    update.classed('dragged', (d) => d.dragged)
    update.classed('isHighlighted', (d) =>
      highlightedDeviceIds?.find((id) => d.id === id)
    )
  }

  /**
   * The enter & update methods in this file are a total mess. Basically we
   * seem to be creating each of these icons from scratch each time we need
   * them, but they were hardcoded to always be a set size.
   * I fed each of these to Claude and asked that they be made resizable. While
   * they work, it's SUPER messy.
   *
   * Tbh, I don't think it's worth spending any more time to try and clean
   * this up as this whole thing needs to be re-worked, and I've created
   * ENG-20090 to track this work
   */

  // Alarm
  const alarmEnter = (enter, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 24

    // Base dimensions that will be scaled
    const baseCircleRadius = 22

    const g = enter
      .append('g')
      .attr('class', 'alarm')
      .classed('editOn', (d) => d.isEditMode)
      .attr('id', (d) => `device-${d.id}`)
      .attr(
        'transform',
        (d) =>
          `translate(${d.projectedX}, ${
            isRebrand ? d.projectedY : d.projectedX
          })`
      )
      .on('click', (event, d) => {
        onDeviceClicked(d)
        event.stopPropagation()
      })
      .on('mouseover', (event, d) => {
        onDeviceHover(d, d.id, 'alarm')
      })
      .on('mouseout', (event, d) => {
        onDeviceHoverOut(d, d.id, 'alarm')
      })
      .call(handleDeviceDrag)

    if (isRebrand) {
      appendRebrandDeviceMarker(g, 'alarm', iconSize)
      return
    }

    g.append('circle')
      .attr('class', 'cameraCircle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', baseCircleRadius * scaleFactor)

    g.append('image')
      .attr('class', 'alarmIconSvg')
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('href', 'alarm.png')
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)
  }

  const alarmUpdate = (update, iconSize) => {
    if (isRebrand) {
      updateRebrandDeviceMarker(update, iconSize, 'alarm')
    } else {
      // Calculate scale factor based on the provided icon size
      const scaleFactor = iconSize / 24

      update
        .select('.alarmIconSvg')
        .attr('width', `${iconSize}px`)
        .attr('height', `${iconSize}px`)
        .attr('x', -iconSize / 2)
        .attr('y', -iconSize / 2)

      update.select('.cameraCircle').attr('r', 22 * scaleFactor)
    }

    update.attr(
      'transform',
      (d) => `translate(${d.projectedX}, ${d.projectedY})`
    )
    update.attr('id', (d) => {
      return `device-${d.id}`
    })

    update.classed('invalidPosition', (d) => d.invalidPosition)
    update.classed('editOn', (d) => d.isEditMode)
    update.classed('dragged', (d) => d.dragged)
    update.classed('isHighlighted', (d) =>
      highlightedDeviceIds?.find((id) => d.id === id)
    )
  }
  // endregion

  const connectionEnter = (enter, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 24

    // Base dimensions that will be scaled
    const baseCircleRadius = 16
    const baseIconWidth = 24
    const baseIconHeight = 15
    const baseIconOffsetX = 12
    const baseIconOffsetY = 8

    const g = enter
      .append('g')
      .attr('class', 'connection')
      .classed('isConnected', (d) => d.isConnected)
      .classed('tailgateEnabled', (d) => d.isTailgateEnabled)
      .attr('id', (d) => `connection-${getTailgateConnectionElementId(d)}`)
      .on('click', (event, d) => {
        onConnectionButtonClicked(d)
      })
      .on('mouseover', (event, d) => {
        onDeviceHover(d, d.id, 'tailgate')
      })
      .on('mouseout', (event, d) => {
        onDeviceHoverOut(d, d.id, 'tailgate')
      })

    g.append('path')
      .attr('class', 'connectionLine')
      .attr('d', (d) => connectionLine(d.lineCoords))
    // Note: The line path itself doesn't need scaling as its coordinates
    // are determined by the connections between devices

    g.append('circle')
      .attr('class', 'connectionCircle')
      .attr('cx', (d) => d.midX)
      .attr('cy', (d) => d.midY)
      .attr('r', isRebrand ? iconSize / 2 : baseCircleRadius * scaleFactor)

    if (isRebrand) {
      appendRebrandAssociationMarker(g, iconSize)
      return
    }

    g.append('image')
      .attr('class', 'connectionImage')
      .attr('width', `${baseIconWidth * scaleFactor}px`)
      .attr('height', `${baseIconHeight * scaleFactor}px`)
      .attr('href', getConnectionIcon)
      .attr('x', (d) => d.midX - baseIconOffsetX * scaleFactor)
      .attr('y', (d) => d.midY - baseIconOffsetY * scaleFactor)
  }

  const connectionUpdate = (update, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 24

    // Base dimensions that will be scaled
    const baseIconOffsetX = 12
    const baseIconOffsetY = 8

    update
      .classed('isConnected', (d) => d.isConnected)
      .classed('tailgateEnabled', (d) => d.isTailgateEnabled)

    update.select('path').attr('d', (d) => connectionLine(d.lineCoords))

    if (isRebrand) {
      updateRebrandAssociationMarker(update, iconSize)
      return
    }

    update
      .select('image')
      .attr('x', (d) => d.midX - baseIconOffsetX * scaleFactor)
      .attr('y', (d) => d.midY - baseIconOffsetY * scaleFactor)
      .attr('href', getConnectionIcon)
      .attr('width', `${24 * scaleFactor}px`)
      .attr('height', `${15 * scaleFactor}px`)

    update
      .select('.connectionCircle')
      .attr('cx', (d) => d.midX)
      .attr('cy', (d) => d.midY)
      .attr('r', 16 * scaleFactor)
  }

  const checkpointEnter = (enter, size) => {
    // Calculate derived dimensions based on size
    const halfSize = size / 2
    const tooltipHeight = size * 0.5
    const tooltipYPosition = -size * 1.25
    const tooltipTextYPosition = tooltipYPosition + tooltipHeight / 2 + 4
    const handleSize = size * 0.57 // ~32px when size is 56
    const handleYPosition = -size * 0.75
    const leftHandleXPosition = -size * 0.96 // ~-54px when size is 56
    const rightHandleXPosition = size * 0.5 // ~28px when size is 56
    const paddingForText = size * 0.36 // ~20px when size is 56

    const g = enter
      .append('g')
      .attr('class', 'checkpoint')
      .classed('editOn', (c) => c.isEditMode)
      .attr('id', (c) => `checkpoint-${c.id}`)
      .attr(
        'transform',
        (c) =>
          `translate(${isRebrand ? c.projectedX : c.x}, ${
            isRebrand ? c.projectedY : c.x
          })`
      )
      .call(
        handleCheckPointDeviceDrag(
          (d) => onDeviceDragStart(d),
          (d) => onDeviceUpdated(d),
          (d) => onDeviceReleased(d)
        )
      )

    const addCheckpointInteractions = (selection) =>
      selection
        .on('mouseover', (event, c) => {
          d3.select(`#checkpointBox-${c.id}`).style('visibility', 'visible')
          d3.select(`#checkpointBoxText-${c.id}`).style('visibility', 'visible')

          d3.select(`#checkpointBoxText-${c.id}`).text((c) => c.deviceName)
          const checkpointTextWidth =
            d3.select(`#checkpointBoxText-${c.id}`).node()?.getBBox()?.width +
            paddingForText
          d3.select(`#checkpointBox-${c.id}`)
            .attr('width', `${checkpointTextWidth}`)
            .attr('x', -checkpointTextWidth / 2)
        })
        .on('mouseout', (event, c) => {
          d3.select(`#checkpointBox-${c.id}`).style('visibility', 'hidden')
          d3.select(`#checkpointBoxText-${c.id}`).style('visibility', 'hidden')
        })
        .on('click', (event, c) => {
          onDeviceClicked(c)
          event.stopPropagation()
        })

    const appendCheckpointAction = ({
      className,
      color,
      legacyHref,
      rebrandHref,
      x,
    }) => {
      if (!isRebrand) {
        return g
          .append('image')
          .attr('class', className)
          .attr('width', `${handleSize}px`)
          .attr('height', `${handleSize}px`)
          .attr('href', legacyHref)
          .attr('x', x)
          .attr('y', handleYPosition)
          .attr('visibility', isHandleVisible)
      }

      // Center the icon glyph within the checkpoint action handle.
      const glyphOffset =
        (handleSize - FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE) / 2
      const action = g
        .append('g')
        .attr('class', className)
        .attr('color', color)
        .attr('transform', `translate(${x}, ${handleYPosition})`)
        .attr('visibility', isHandleVisible)

      action
        .append('rect')
        .attr('fill', themedColors.checkpointActionHitArea)
        .attr('height', handleSize)
        .attr('width', handleSize)

      action
        .append('use')
        .attr('href', rebrandHref)
        .attr('transform', `translate(${glyphOffset}, ${glyphOffset})`)

      return action
    }

    if (isRebrand) {
      const checkpointPin = g
        .append('g')
        .attr('class', 'checkpointIconSvg checkpointMarker')
        .attr('opacity', (d) =>
          highlightedDeviceIds?.includes(d.id) ? 1.0 : 0.5
        )

      addCheckpointInteractions(checkpointPin)

      checkpointPin
        .append('path')
        .attr('class', 'checkpointPinShape')
        .attr(
          'd',
          'M12 31C12 31 1 18 1 12C1 5.92 5.92 1 12 1C18.08 1 23 5.92 23 12C23 18 12 31 12 31Z'
        )
        .attr('transform', 'translate(-12 -31)')
        .attr('fill', canvasMarkerColors.markerBackground)
        .attr('stroke', canvasMarkerColors.markerRing)
        .attr('stroke-width', 1.5)

      checkpointPin
        .append('g')
        .attr('class', 'checkpointGuardGlyph')
        .attr('color', canvasMarkerColors.markerGlyph)
        .attr('transform', 'translate(-6.5 -26.9) scale(0.65)')
        .style('display', (d) =>
          tourStopDeviceIds?.includes(d.id) ? 'none' : null
        )
        .append('use')
        .attr('href', '#floor-plan-guard-glyph')

      checkpointPin
        .append('text')
        .attr('class', 'checkpointTourOrdinal')
        .attr('fill', canvasMarkerColors.markerGlyph)
        .attr('font-family', fontFamilies.soehneKraftig)
        .attr('font-size', 14)
        .attr('font-weight', 500)
        .attr('text-anchor', 'middle')
        .attr('y', -12)
        .text((d) => getTourStopOrdinal(d.id))
        .style('display', (d) =>
          tourStopDeviceIds?.includes(d.id) ? null : 'none'
        )
    } else {
      const checkpointImage = g
        .append('image')
        .attr('class', 'checkpointIconSvg')
        .attr('width', `${size}px`)
        .attr('height', `${size}px`)
        .attr('href', 'checkpoint.png')
        .attr('opacity', 1.0)
        .attr('x', -halfSize)
        .attr('y', -halfSize)

      addCheckpointInteractions(checkpointImage)
    }

    //Checkpoint ToolTip
    g.append('rect')
      .attr('id', (c) => `checkpointBox-${c.id}`)
      .attr('class', 'checkpointToolTipBox')
      .attr('y', tooltipYPosition)
      .attr('rx', `${size * 0.07}px`) // 4px when size is 56
      .attr('height', `${tooltipHeight}px`)
      .style('visibility', 'hidden')

    g.append('text')
      .attr('id', (c) => `checkpointBoxText-${c.id}`)
      .attr('class', 'checkpointToolTipText')
      .attr('dy', tooltipTextYPosition)
      .attr('text-anchor', 'middle')
      .text((c) => c.deviceName)
      .style('visibility', 'hidden')

    // Right ToolTip
    g.append('rect')
      .attr('id', (c) => `moveCircleBox-${c.id}`)
      .attr('class', 'checkpointCircleToolTipR')
      .attr('x', 0)
      .attr('y', tooltipYPosition)
      .attr('rx', `${size * 0.07}px`)
      .attr('width', getEditCheckPointWidth)
      .attr('height', `${tooltipHeight * 0.86}px`)
      .style('visibility', 'hidden')

    g.append('text')
      .attr('id', (c) => `moveCircleText-${c.id}`)
      .attr('class', 'checkpointToolTipR')
      .attr('dx', size * 0.18) // 10px when size is 56
      .attr('dy', tooltipTextYPosition)
      .attr('text-anchor', 'right')
      .text('End Moving')
      .style('visibility', 'hidden')

    // Left ToolTip
    const leftTooltipWidth = size * 3.04 // ~170px when size is 56
    g.append('rect')
      .attr('id', (c) => `removeCircleBox-${c.id}`)
      .attr('class', 'checkpointCircleToolTipL')
      .attr('x', -leftTooltipWidth)
      .attr('y', tooltipYPosition)
      .attr('rx', `${size * 0.07}px`)
      .attr('width', `${leftTooltipWidth}px`)
      .attr('height', `${tooltipHeight * 0.86}px`)
      .style('visibility', 'hidden')

    g.append('text')
      .attr('id', (c) => `removeCircleText-${c.id}`)
      .attr('class', 'checkpointToolTipL')
      .attr('dx', -(leftTooltipWidth - size * 0.18)) // -160 when size is 56
      .attr('dy', tooltipTextYPosition)
      .attr('text-anchor', 'left')
      .text('Remove from map')
      .style('visibility', 'hidden')

    appendCheckpointAction({
      className: 'removeIconSvg',
      color: themedColors.checkpointRemoveIcon,
      legacyHref: 'remove.png',
      rebrandHref: FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS.remove,
      x: leftHandleXPosition,
    })
      .attr('opacity', getRemoveIconOpacity)
      .on('mouseover', (event, c) => {
        d3.select(`#removeCircleBox-${c.id}`).style('visibility', 'visible')
        d3.select(`#removeCircleText-${c.id}`).style('visibility', 'visible')
      })
      .on('mouseout', (event, c) => {
        d3.select(`#removeCircleBox-${c.id}`).style('visibility', 'hidden')
        d3.select(`#removeCircleText-${c.id}`).style('visibility', 'hidden')
      })
      .on('click', (event, d) => {
        if (!d.editCheckpointPos) {
          onRemoveCheckpoint(d)
          event.stopPropagation()
        }
      })

    appendCheckpointAction({
      className: 'editIconSvg',
      color: getEditCheckPointColor(themedColors),
      legacyHref: getEditCheckPointIcon,
      rebrandHref: getEditCheckPointGlyph,
      x: rightHandleXPosition,
    })
      .attr('opacity', FLOOR_PLAN_CHECKPOINT_ACTION_OPACITY.ACTIVE)
      .on('mouseover', (event, c) => {
        d3.select(`#moveCircleBox-${c.id}`).style('visibility', 'visible')
        d3.select(`#moveCircleText-${c.id}`).style('visibility', 'visible')
      })
      .on('mouseout', (event, c) => {
        d3.select(`#moveCircleBox-${c.id}`).style('visibility', 'hidden')
        d3.select(`#moveCircleText-${c.id}`).style('visibility', 'hidden')
      })
      .on('click', (event, d) => {
        onMoveCheckpoint(d)
        event.stopPropagation()
      })
  }

  const checkpointControlUpdate = (update) => {
    update.attr(
      'transform',
      (d) => `translate(${d.projectedX}, ${d.projectedY})`
    )
    update.classed('invalidPosition', (d) => d.invalidPosition)
    update.classed('editOn', (d) => d.isEditMode)
    update.classed('dragged', (d) => d.dragged)

    update.select('.checkpointCircleToolTipL').attr('id', (d) => {
      return `removeCircleBox-${d.id}`
    })
    update.select('.checkpointToolTipL').attr('id', (d) => {
      return `removeCircleText-${d.id}`
    })

    update.select('.checkpointCircleToolTipR').attr('id', (d) => {
      return `moveCircleBox-${d.id}`
    })

    update.select('.checkpointToolTipR').attr('id', (d) => {
      return `moveCircleText-${d.id}`
    })

    update.select('.checkpointToolTipBox').attr('id', (d) => {
      return `checkpointBox-${d.id}`
    })

    update.select('.checkpointToolTipText').attr('id', (d) => {
      return `checkpointBoxText-${d.id}`
    })

    update.select('.removeIconSvg').attr('opacity', getRemoveIconOpacity)
    if (isRebrand) {
      update
        .select('.checkpointPinShape')
        .attr('fill', canvasMarkerColors.markerBackground)
        .attr('stroke', canvasMarkerColors.markerRing)

      update
        .select('.checkpointGuardGlyph')
        .attr('color', canvasMarkerColors.markerGlyph)
        .style('display', (d) =>
          tourStopDeviceIds?.includes(d.id) ? 'none' : null
        )

      update
        .select('.checkpointTourOrdinal')
        .attr('fill', canvasMarkerColors.markerGlyph)
        .text((d) => getTourStopOrdinal(d.id))
        .style('display', (d) =>
          tourStopDeviceIds?.includes(d.id) ? null : 'none'
        )

      update
        .select('.removeIconSvg')
        .attr('color', themedColors.checkpointRemoveIcon)
      update
        .select('.editIconSvg')
        .attr('color', getEditCheckPointColor(themedColors))
      update.select('.editIconSvg use').attr('href', getEditCheckPointGlyph)
    } else {
      update.select('.checkpointIconSvg').attr('href', getCheckPointIcon)
      update.select('.editIconSvg').attr('href', getEditCheckPointIcon)
    }
    update.select('.removeIconSvg').attr('visibility', isHandleVisible)
    update
      .select('.editIconSvg')
      .attr('opacity', FLOOR_PLAN_CHECKPOINT_ACTION_OPACITY.ACTIVE)
      .attr('visibility', isHandleVisible)

    update.select('.checkpointToolTipR').text(getEditCheckPointText)
    update
      .select('.checkpointCircleToolTipR')
      .attr('width', getEditCheckPointWidth)

    update
      .select('.checkpointIconSvg')
      .attr('opacity', (d) =>
        highlightedDeviceIds?.includes(d.id) ? 1.0 : 0.5
      )
  }

  const renderDevices = () => {
    if (!devices) return

    const cameras = devices.filter((d) => d.deviceType === DeviceType.Camera)
    const doors = devices.filter((d) => d.deviceType === DeviceType.Door)
    const alarms = devices.filter((d) => d.deviceType === DeviceType.Alarm)

    // Alarms
    d3.select('.alarm-group')
      .selectAll(isRebrand ? 'g.alarm' : 'g')
      .data(alarms, (d) => d.id)
      .join(
        (enter) => alarmEnter(enter, floorPlanIconSize),
        (update) => alarmUpdate(update, floorPlanIconSize),
        (exit) => exit.remove()
      )

    // Cameras
    d3.select('.camera-group')
      .selectAll(isRebrand ? 'g.camera' : 'g')
      .data(cameras, (d) => d.id)
      .join(
        (enter) =>
          cameraEnter(
            enter,
            isRebrand ? floorPlanIconSize : floorPlanIconSize * 2
          ),
        (update) =>
          cameraUpdate(
            update,
            isRebrand ? floorPlanIconSize : floorPlanIconSize * 2
          ),
        (exit) => exit.remove()
      )

    // Doors
    d3.select('.door-group')
      .selectAll(isRebrand ? 'g.door' : 'g')
      .data(doors, (d) => d.id)
      .join(
        (enter) => doorEnter(enter, floorPlanIconSize),
        (update) => doorUpdate(update, floorPlanIconSize),
        (exit) => exit.remove()
      )

    // When associating devices
    const connectionIconSize = isRebrand
      ? floorPlanIconSize * (28 / 24)
      : floorPlanIconSize
    d3.select('.connections-group')
      .selectAll(isRebrand ? 'g.connection' : 'g')
      .data(connections, (d) => d.id)
      .join(
        (enter) => connectionEnter(enter, connectionIconSize),
        (update) => connectionUpdate(update, connectionIconSize),
        (exit) => exit.remove()
      )

    // Controls are smaller than other icons. Make sure user doesn't make them too small
    const controlSize = Math.max(20, floorPlanIconSize)

    d3.select('.camera-tools-group')
      .selectAll(isRebrand ? 'g.cameraControl' : 'g')
      .data(cameras, (d) => d.id)
      .join(
        (enter) => cameraControlEnter(enter, controlSize),
        (update) => cameraControlUpdate(update, controlSize),
        (exit) => exit.remove()
      )

    // TODO - In future we'll need to scale checkpoints, but it'll require
    //  some additional work to get the pin to go where the location is, not
    //  below
    // For some reason checkpoints require a much larger size, but don't look larger
    // const checkpointScale = 56 / defaultSize

    d3.select('.checkpoint-group')
      .selectAll(isRebrand ? 'g.checkpoint' : 'g')
      .data(checkpoints, (d) => d.id)
      .join(
        (enter) => checkpointEnter(enter, 56),
        (update) => checkpointControlUpdate(update),
        (exit) => exit.remove()
      )
  }

  const render = (svgElement, imageElement) => {
    const svg = d3.select(svgElement)
    const { scale } = getMapValues(svgElement, imageElement)
    svg.call(getZoom(scale * 0.5))
  }

  const getMapValues = (svgElement, imageElement) => {
    const img = d3.select(imageElement)
    const imageWidth = Number(img.attr('width'))
    const imageHeight = Number(img.attr('height'))

    const svg = d3.select(svgElement)
    const chartWidth = Number(svg.attr('width'))
    const chartHeight = Number(svg.attr('height'))

    const mapWidth = imageWidth + IMAGE_PADDING
    const mapHeight = imageHeight + IMAGE_PADDING
    const scale = Math.min(chartWidth / mapWidth, chartHeight / mapHeight)
    return { chartWidth, chartHeight, mapWidth, mapHeight, scale }
  }

  const centerZoom = (svgElement, imageElement) => {
    if (!svgElement) return

    const svg = d3.select(svgElement)
    const { chartWidth, chartHeight, mapWidth, mapHeight, scale } =
      getMapValues(svgElement, imageElement)
    let transform = d3.zoomIdentity
    // move to center point
    transform = transform.translate(chartWidth / 2, chartHeight / 2)
    // apply scale
    transform = transform.scale(scale)
    // center content
    transform = transform.translate(
      IMAGE_PADDING / 2 - mapWidth / 2,
      IMAGE_PADDING / 2 - mapHeight / 2
    )
    getZoom(scale).transform(svg, transform)
  }

  useEffect(renderDevices, [
    devices,
    checkpoints,
    connections,
    floorPlanIconSize,
    visualVariant,
    colorScheme,
    highlightedDeviceIds,
    tourStopDeviceIds,
  ])

  return { render, centerZoom }
}
